import { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { ComponentType, IDashboardComponent, IGripPosition } from "../types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Tab,
  Tabs,
  TextField
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Select from "@mui/material/Select";
import { cloneObject, getUID } from "../../../system/utils";
import TabPanel from "../../../components/tabPanel";
import { useSelector } from "react-redux";
import { ISelectedNode, IState } from "../../../types";
import VariablesTable from "./widgets/indicators/variablesTable";
import VariablesTableRadar from "./widgets/radar/variablesTable";
import { getComponentConfig } from "../utils";
import EventConfigPanel from "./widgets/events/eventConfigPanel";
import AcquisitionPanel from "./widgets/chart/acquisitionPanel";
import VariablesTableDoughnut from "./widgets/doughnut/variablesTable";
import VariablesTableGauge from "./widgets/gauge/variablesTable";
import CustomComponentConfig from "./widgets/customWidgets/customConfigSelector";
import ColorPicker from "../../../components/colorPicker";


interface IProps {
  component?: IDashboardComponent<any>;
  onClose: () => void;
  onAccept: (newComponent: IDashboardComponent<any>) => void;
  setPanelPosition: (panelPosition: IGripPosition | null) => void;
  customData?: {
    column: number;
    row: number;
    columnSpan: number;
    rowSpan: number;
    componentType: ComponentType;
  };
}

const maxGridValue = 13;

const AddComponent = ({ component, onClose, onAccept, setPanelPosition, customData }: IProps): ReactElement => {
  const [tab, setTab] = useState(0);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [ selectedBgColor, setSelectedBgColor ] = useState<string>(component?.background ?? "#00000000");
  const [ selectedColor, setSelectedColor ] = useState<string>(component?.foreground ?? "#FFFFFFFF");
  const [data, setData] = useState<IDashboardComponent<any>>(component ? cloneObject(component) : {
    key: getUID(),
    title: "",
    componentType: ComponentType.Indicators,
    column: 1,
    row: 1,
    columnSpan: 2,
    rowSpan: 2,
    ...customData,
    config: getComponentConfig(customData ? customData.componentType : ComponentType.Indicators)
  });

  useEffect(() => {
    setPanelPosition({
      column: data.column,
      columnSpan: data.columnSpan,
      row: data.row,
      rowSpan: data.rowSpan,
    });
  }, [data]);

  const onChange = (field: keyof IDashboardComponent<any>, value: any) => {
    if (field === "componentType") {
      setData({ ...data, [field]: value, config: getComponentConfig(value) });
    } else if (field === "column") {
      const valid = data.columnSpan + value <= maxGridValue;

      setData({
        ...data,
        [field]: value,
        columnSpan: valid ? data.columnSpan : Math.min(data.columnSpan, maxGridValue - value)
      });
    } else if (field === "columnSpan") {
      const valid = data.column + value <= maxGridValue;

      setData({ ...data, [field]: valid ? value : maxGridValue - data.column  });
    } else if (field === "row") {
      const valid = data.rowSpan + value <= maxGridValue;

      setData({
        ...data,
        [field]: value,
        rowSpan: valid ? data.rowSpan : Math.min(data.rowSpan, maxGridValue - value) });
    } else if (field === "rowSpan") {
      const valid = data.row + value <= 13;

      setData({ ...data, [field]: valid ? value : maxGridValue - data.row  });
    } else if(field === "foreground") {
      data[field] = `#${value.hex}`;
      setSelectedColor(value);
    } else if(field === "background") {
      data[field] = `#${value.hex}`;
      setSelectedBgColor(value);
    } else {
      setData({ ...data, [field]: value });
    }
  };

  const onInternalAccept = () => {
    onAccept(data);
  };

  const handleTabChange = (_event: ChangeEvent<any>, newValue: number) => {
    setTab(newValue);
  };

  return (
    <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={true} maxWidth="xl" keepMounted={true}>
      <DialogTitle id="customized-dialog-title">
        Add Component
      </DialogTitle>

      <DialogContent dividers={true}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered={true}
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          <Tab label="General"/>
          <Tab label="Config"/>
        </Tabs>

        <TabPanel value={tab} index={0}>
          <div className="display-flex-column p-1" style={{ width: 400 }}>
            <TextField
              className="m-1"
              variant="standard"
              placeholder={"Title"}
              label={"Title"}
              value={data.title}
              onChange={(event) => onChange("title", event.target.value)}/>

            <FormControl className="m-1">
              <InputLabel style={{ transform: "translate(0px, 0px) scale(0.75) !important" }}>Component Type</InputLabel>
              <Select
                variant="standard"
                value={data.componentType}
                displayEmpty={true}
                onChange={(event) => onChange("componentType", event.target.value as ComponentType)}>
                <MenuItem value={ComponentType.Events}>Events</MenuItem>
                <MenuItem value={ComponentType.AcquisitionChart}>Trending</MenuItem>
                <MenuItem value={ComponentType.Indicators}>Indicators</MenuItem>
                <MenuItem value={ComponentType.Radar}>Radar</MenuItem>
                <MenuItem value={ComponentType.Doughnut}>Doughnut</MenuItem>
                <MenuItem value={ComponentType.Gauge}>Gauge</MenuItem>
                <MenuItem value={ComponentType.Text}>Text</MenuItem>
                <MenuItem value={ComponentType.Custom}>Custom</MenuItem>
              </Select>
            </FormControl>

            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <TextField
                className="m-1"
                label={"Column"}
                variant="standard"
                size="small"
                value={data.column}
                type="number"
                slotProps={{
                  input: { inputProps: { min: 1, max: 12 } }
                }}
                onChange={(event) => onChange("column", parseInt(event.target.value as string, 10))}
              />

              <TextField
                className="m-1"
                label={"Column Span"}
                variant="standard"
                size="small"
                value={data.columnSpan}
                type="number"
                slotProps={{
                  input: { inputProps: { min: 1, max: 12 } }
                }}
                onChange={(event) => onChange("columnSpan", parseInt(event.target.value as string, 10))}
              />
            </div>

            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <TextField
                className="m-1"
                label={"Row"}
                variant="standard"
                size="small"
                value={data.row}
                type="number"
                slotProps={{
                  input: { inputProps: { min: 1, max: 12 } }
                }}
                onChange={(event) => onChange("row", parseInt(event.target.value as string, 10))}
              />

              <TextField
                className="m-1"
                label={"Row Span"}
                variant="standard"
                size="small"
                value={data.rowSpan}
                type="number"
                slotProps={{
                  input: { inputProps: { min: 1, max: 12 } }
                }}
                onChange={(event) => onChange("rowSpan", parseInt(event.target.value as string, 10))}
              />
            </div>
          </div>

          <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div
              className="ml-2 mr-2 mb-2 flex-grow-color-picker"
              style={{
                display: "flex",
                flexDirection: "column",
                borderBottom: "1px solid rgba(255, 255, 255, .7)"
              }}>
              <InputLabel style={{ transform: "translate(0px, 6px) scale(0.75)" }}>Background</InputLabel>
              <div className="display-grid" style={{ gridTemplateColumns: "1fr auto" }}>
                <ColorPicker color={selectedBgColor}
                  onChange={(newColor) => onChange("background", newColor)} />
              </div>
            </div>

            <div
              className="ml-2 mr-2 mb-2 flex-grow-color-picker"
              style={{
                display: "flex",
                flexDirection: "column",
                borderBottom: "1px solid rgba(255, 255, 255, .7)"
              }}>
              <InputLabel style={{ transform: "translate(0px, 6px) scale(0.75)" }}>Font</InputLabel>
              <div className="display-grid" style={{ gridTemplateColumns: "1fr auto" }}>
                <ColorPicker color={selectedColor} onChange={(newColor) => onChange("foreground", newColor)} />
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {data.componentType === ComponentType.Indicators && <VariablesTable component={data} updateComponent={setData} zoneID={selectedNode.nodeID}/>}
          {data.componentType === ComponentType.Events && <EventConfigPanel component={data} updateComponent={setData} zoneID={selectedNode.nodeID}/>}
          {data.componentType === ComponentType.AcquisitionChart && <AcquisitionPanel component={data} updateComponent={setData} selectedNode={selectedNode}/>}
          {data.componentType === ComponentType.Radar && <VariablesTableRadar component={data} updateComponent={setData} zoneID={selectedNode.nodeID} />}
          {data.componentType === ComponentType.Doughnut && <VariablesTableDoughnut component={data} updateComponent={setData} zoneID={selectedNode.nodeID} />}
          {data.componentType === ComponentType.Gauge && <VariablesTableGauge component={data} updateComponent={setData} zoneID={selectedNode.nodeID} />}
          {data.componentType === ComponentType.Custom && <CustomComponentConfig component={data} updateComponent={setData} />}
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onInternalAccept} variant="contained" color="primary" startIcon={<CheckIcon/>}>
          Accept
        </Button>
        <Button onClick={onClose} variant="contained" color="secondary" startIcon={<CloseIcon/>}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddComponent;
